<template>
  <div v-if="message" class="campaign-inactive">
    <Head>
      <body class="site--404" />
    </Head>
    <h2 :style="messageStyle">{{ message.headline }}</h2>
    <!-- nosem: javascript.vue.security.audit.xss.templates.avoid-v-html.avoid-v-html -->
    <h4 class="message-body" :style="messageStyle" v-html="message.description"></h4>
  </div>
  <Campaign v-if="!isLoading && state && state.config && !message" :key="state.config.campaign_id" :data="state" />
</template>

<script lang="ts">
import { computed, CSSProperties, defineComponent, getCurrentInstance, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import { ConfigData } from '@/Config';
import useAxios from '@/hooks/useAxios';
import Campaign from '@/components/Campaign.vue';
import { CampaignData } from '@/typings/interfaces/data/campaign';
import { useStore } from '@/store';
import { recursivelyWaitForPromises } from '@/utilities/virtualDom';
import { isSsr } from '@/utilities/Utilities';
import { ApiMessageData } from '@/typings/interfaces/api';

export default defineComponent({
  name: 'CampaignIframeQueueView',
  components: {
    Campaign
  },
  async serverPrefetch() {
    await this.getCampaignData();
    await this.readyPromise;
  },
  setup() {
    const route = useRoute();
    const store = useStore();

    const message = ref<ApiMessageData | undefined>();
    const state = ref<CampaignData>((store.getters['campaign/data'] || {}) as CampaignData);
    const isLoading = ref(!store.getters['campaign/data']);
    const routeHashId = route.params.hash.toString().split(':')[1];

    const getCampaignData = async () => {
      const { fetchData } = useAxios<CampaignData>(
        `${ConfigData.apiEndpoint}/api/v1/campaign/view/iframe/${routeHashId}?vue=1`
      );

      try {
        const response = await fetchData();

        if (response) {
          store.commit('campaign/setData', response);
          state.value = response;
        }
      } catch (e) {
        // In case API responds with a message to show (e.g. 404) we need to display this.
        // This is done by accepting the message incoming & putting it in a ref that will display the data.
        if (e?.response?.data?.message?.description) {
          // TODO: Investigate if we can type this more strongly. Not sure if we can.
          message.value = e.response.data.message as ApiMessageData;
        }
        // In case API responds with a redirect we need to display handle this.
        if (e?.response?.data?.redirect && !isSsr()) {
          // eslint-disable-next-line security/detect-non-literal-fs-filename
          window.open(e?.response?.data?.redirect.url, e?.response?.data?.redirect.target);
        }

        // TODO: Implement some sort of error handling for when campaign cannot be loaded
      }

      isLoading.value = false;
    };

    let readyPromiseResolve: (() => void) | undefined;

    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    const currentInstance = getCurrentInstance();

    if (isLoading.value) {
      getCampaignData();
    }

    const messageStyle = computed<CSSProperties>(() => {
      if (message.value) {
        return {
          textAlign: message.value.alignment,
          ...(message.value.textColor && { color: message.value.textColor })
        };
      }

      return {};
    });

    nextTick(async () => {
      if (currentInstance?.vnode) {
        await recursivelyWaitForPromises(currentInstance.vnode, 'onBeforeEnter', 3);
      }

      if (readyPromiseResolve) {
        readyPromiseResolve();
      }
    });

    return {
      state,
      isLoading,
      getCampaignData,
      readyPromise,
      message,
      messageStyle
    };
  }
});
</script>
